/**
 * HamburgerSheet — slide-in side panel hiding everything that doesn't
 * belong on the resting top bar: settings, profile, premium, saved,
 * leaderboard, support, etc.
 *
 * iter-84: restructured to match the strategy doc with three sections
 *   PRIMARY   — home, discover, create, leaderboard
 *   SECONDARY — saved (signed-in), my vibe, premium
 *   UTILITY   — appearance, support, privacy & terms
 * Plus the persistent "Creators & Business — Coming Soon" tile at the
 * bottom, and a stat-line (`{N} cards shared`) under the user's name.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
    X, User, Crown, Bookmark, Trophy, Sparkles, Heart, Palette,
    LifeBuoy, LogOut, ChevronRight, Home, Compass, Wand2, ArrowRight, Shield,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../../lib/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
    {
        label: "Primary",
        items: [
            { key: "home",        label: "Home",         icon: Home,    to: "/" },
            { key: "discover",    label: "Discover",     icon: Compass, to: "/discover" },
        ],
    },
    {
        label: "Secondary",
        items: [
            // iter-101 — renamed "saved cards" → "My Cards" per user direction
            { key: "saved",       label: "My Cards",     icon: Bookmark, to: "/saved", authOnly: true },
            { key: "my-vibe",     label: "My Vibe",      icon: Heart,    to: "/my-vibe" },
            // iter-104 — "Premium" removed. It's a state, not a destination.
            // Premium surfaces contextually from locked tiles / pour-limit CTA.
            // "Create" merged into Home (same page).
        ],
    },
    {
        label: "Utility",
        items: [
            // iter-101 — Appearance + Leaderboard SHELVED for MVP (light theme
            // not optimized; leaderboard feature deferred).
            { key: "support",     label: "Support",      icon: LifeBuoy, to: "/about" },
            { key: "privacy",     label: "Privacy & Terms", icon: Shield, to: "/privacy" },
        ],
    },
];

export default function HamburgerSheet({ open, onClose }) {
    const navigate = useNavigate();
    const { user, isPremium, logout } = useAuth() || {};
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (open) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    // Fetch the user's "cards shared" stat lazily — only when the sheet
    // is opened and the user is signed in. Best-effort, silent on failure.
    useEffect(() => {
        if (!open || !user) return;
        let cancelled = false;
        axios
            .get(`${API}/me/stats`, { withCredentials: true })
            .then(({ data }) => { if (!cancelled) setStats(data); })
            .catch(() => { /* silent */ });
        return () => { cancelled = true; };
    }, [open, user]);

    const go = (to) => { onClose?.(); navigate(to); };

    const displayName = (user?.name || user?.email || "you").split("@")[0];

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={onClose}
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                        data-testid="hamburger-backdrop"
                    />
                    <motion.aside
                        initial={{ x: "-100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "-100%" }}
                        transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed top-0 left-0 bottom-0 z-50 w-[82%] max-w-[340px] bg-[#0d0d0f] border-r border-white/8 overflow-y-auto"
                        data-testid="hamburger-sheet"
                    >
                        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
                            <span className="font-display text-base tracking-[0.18em] uppercase text-white/95">
                                moodcard
                            </span>
                            <button
                                onClick={onClose}
                                aria-label="close menu"
                                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/85 transition-colors"
                                data-testid="hamburger-close"
                            >
                                <X className="w-4 h-4" strokeWidth={1.6} />
                            </button>
                        </div>

                        {/* Identity */}
                        <div className="px-5 py-4 border-b border-white/6">
                            {user ? (
                                <>
                                    <div
                                        className="font-serif italic text-white text-lg truncate"
                                        data-testid="hamburger-display-name"
                                    >
                                        {displayName}
                                    </div>
                                    <div
                                        className="mt-1 text-[11px] tracking-[0.22em] uppercase text-white/55"
                                        data-testid="hamburger-cards-shared"
                                    >
                                        {typeof stats?.cards_shared === "number"
                                            ? `${stats.cards_shared} cards shared`
                                            : "—"}
                                    </div>
                                    <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-300/25 text-amber-200 text-[10px] tracking-[0.18em] uppercase">
                                        <Crown className="w-3 h-3" />
                                        {isPremium ? "premium" : "free"}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="text-xs uppercase tracking-[0.22em] text-white/45 mb-1.5">guest</div>
                                    <p className="text-white/65 text-sm font-serif italic leading-relaxed mb-3">
                                        sign in to save cards, track remixes, and join challenges.
                                    </p>
                                    <button
                                        onClick={() => {
                                            // iter-105 — `/auth` route doesn't exist; auth runs through
                                            // the global `open-auth-modal` event which redirects to
                                            // Emergent Google OAuth.
                                            onClose?.();
                                            window.dispatchEvent(new CustomEvent("open-auth-modal", {
                                                detail: { source: "hamburger" },
                                            }));
                                        }}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-xs tracking-wide font-medium transition-colors"
                                        data-testid="hamburger-signin"
                                    >
                                        sign in / create account
                                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Sectioned nav */}
                        <nav className="px-2 py-3">
                            {SECTIONS.map((section) => {
                                const visible = section.items.filter((it) => !it.authOnly || user);
                                if (!visible.length) return null;
                                return (
                                    <div key={section.label} className="mb-3">
                                        <div
                                            className="px-3 mt-1 mb-1.5 text-[9px] tracking-[0.32em] uppercase text-white/35"
                                            data-testid={`hamburger-section-${section.label}`}
                                        >
                                            {section.label}
                                        </div>
                                        {visible.map((it) => {
                                            const Icon = it.icon;
                                            return (
                                                <button
                                                    key={it.key}
                                                    onClick={() => go(it.to)}
                                                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 text-left transition-colors"
                                                    data-testid={`hamburger-${it.key}`}
                                                >
                                                    <Icon className="w-4 h-4 text-white/65" strokeWidth={1.6} />
                                                    <span className="flex-1 font-sans text-sm text-white/90 tracking-wide">
                                                        {it.label}
                                                    </span>
                                                    <ChevronRight className="w-3.5 h-3.5 text-white/35" strokeWidth={1.6} />
                                                </button>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </nav>

                        {/* Coming Soon · Creators & Business waitlist tile */}
                        <div className="px-3 pb-4">
                            <button
                                onClick={() => go("/creators")}
                                data-testid="hamburger-creators-tile"
                                className="w-full text-left rounded-2xl border border-amber-300/15 bg-amber-400/5 hover:bg-amber-400/10 transition-colors p-4"
                            >
                                <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-amber-200/85">
                                    <Sparkles className="w-3 h-3" strokeWidth={1.8} />
                                    creators & business tools
                                </div>
                                <div className="mt-1.5 font-serif italic text-white text-sm">
                                    coming soon
                                </div>
                                <div className="mt-2 inline-flex items-center gap-1.5 text-amber-200 text-[11px] tracking-wide">
                                    join waitlist
                                    <ArrowRight className="w-3 h-3" strokeWidth={2} />
                                </div>
                            </button>
                        </div>

                        {user && (
                            <div className="px-5 pt-2 pb-6 border-t border-white/6 mt-2">
                                <button
                                    onClick={() => go("/me")}
                                    className="w-full flex items-center gap-2 text-white/65 hover:text-white text-sm transition-colors mb-3"
                                    data-testid="hamburger-profile"
                                >
                                    <User className="w-3.5 h-3.5" strokeWidth={1.6} />
                                    <span className="font-sans tracking-wide">profile</span>
                                </button>
                                <button
                                    onClick={() => { logout?.(); onClose?.(); }}
                                    className="w-full flex items-center gap-2 text-white/55 hover:text-white/85 text-sm transition-colors"
                                    data-testid="hamburger-logout"
                                >
                                    <LogOut className="w-3.5 h-3.5" strokeWidth={1.6} />
                                    <span className="font-serif italic">sign out</span>
                                </button>
                            </div>
                        )}

                        {/* Footer */}
                        <div className="px-5 pb-6 mt-1 border-t border-white/4 pt-5 text-center">
                            <div className="font-display text-xs tracking-[0.32em] uppercase text-white/45">moodcard</div>
                            <div className="mt-1 font-serif italic text-[11px] text-white/35">
                                express. connect. inspire.
                            </div>
                        </div>
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
}
